/**
 * Document Preview Modal Component
 * Displays uploaded documents (PDF, DOCX, TXT) in a modal preview
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { guestBlogOrderService } from '../../services/guestBlogOrderService';

interface DocumentPreviewModalProps {
  open: boolean;
  onClose: () => void;
  documentUrl: string;
  fileName: string;
  onDownload?: () => void;
}

function getApiOrigin(): string {
  const base = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  return base.replace(/\/?api\/?$/i, '').replace(/\/$/, '') || 'http://localhost:3001';
}

/** Stored URLs are often `/uploads/...` (API host), not the React dev server. */
function toAbsoluteDocumentUrl(raw: string): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return `${getApiOrigin()}${path}`;
}

/**
 * An "external" URL is an absolute HTTP(S) URL that is NOT on the API origin —
 * i.e. the user pasted a link to Google Docs, Dropbox, Notion, etc., not a file
 * uploaded into our `/uploads/...` directory.
 */
function isExternalUrl(raw: string): boolean {
  if (!raw) return false;
  const trimmed = raw.trim();
  if (!/^https?:\/\//i.test(trimmed)) return false;
  try {
    const apiHost = new URL(getApiOrigin()).host;
    return new URL(trimmed).host !== apiHost;
  } catch {
    return false;
  }
}

function isGoogleDocsUrl(raw: string): boolean {
  return /^https?:\/\/(docs|drive|sheets|slides)\.google\.com\//i.test(raw || '');
}

/** Convert a Google Docs/Sheets/Slides edit URL into an embeddable preview URL. */
function toGoogleDocsEmbedUrl(raw: string): string {
  const url = (raw || '').trim();
  if (!url) return '';
  try {
    // Replace `/edit...` (or `/view...`) with `/preview` and strip query/hash.
    const replaced = url.replace(/\/(edit|view)(\?[^#]*)?(#.*)?$/i, '/preview');
    if (replaced !== url) return replaced;
    // Already ends in /preview or has no /edit segment — append /preview if missing.
    if (/\/preview(\b|$)/i.test(url)) return url.split('?')[0].split('#')[0];
    return `${url.replace(/\/$/, '')}/preview`;
  } catch {
    return url;
  }
}

function friendlyExternalName(raw: string): string {
  const url = (raw || '').trim();
  if (!url) return 'document';
  if (isGoogleDocsUrl(url)) {
    if (/docs\.google\.com\/document/i.test(url)) return 'Google Docs document';
    if (/docs\.google\.com\/spreadsheets|sheets\.google\.com/i.test(url)) return 'Google Sheet';
    if (/docs\.google\.com\/presentation|slides\.google\.com/i.test(url)) return 'Google Slides';
    if (/drive\.google\.com/i.test(url)) return 'Google Drive file';
    return 'Google document';
  }
  try {
    const u = new URL(url);
    return u.host + u.pathname.replace(/\/$/, '');
  } catch {
    return url;
  }
}

const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  open,
  onClose,
  documentUrl,
  fileName,
  onDownload,
}) => {
  const [loading, setLoading] = useState(false);
  const [pdfOverlay, setPdfOverlay] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [docxContent, setDocxContent] = useState<string | null>(null);

  // Get file extension
  const getFileExtension = (filename: string): string => {
    const base = filename.split(/[\\/]/).pop() || filename;
    return base.split('.').pop()?.toLowerCase() || '';
  };

  // Get file type category
  const getFileType = (filename: string): 'pdf' | 'docx' | 'doc' | 'txt' | 'rtf' | 'unsupported' => {
    const ext = getFileExtension(filename);
    switch (ext) {
      case 'pdf':
        return 'pdf';
      case 'docx':
        return 'docx';
      case 'doc':
        return 'doc';
      case 'txt':
        return 'txt';
      case 'rtf':
        return 'rtf';
      default:
        return 'unsupported';
    }
  };

  const external = useMemo(() => isExternalUrl(documentUrl), [documentUrl]);
  const googleDocsEmbed = useMemo(
    () => (external && isGoogleDocsUrl(documentUrl) ? toGoogleDocsEmbedUrl(documentUrl) : ''),
    [external, documentUrl]
  );

  const absoluteUrl = useMemo(() => toAbsoluteDocumentUrl(documentUrl), [documentUrl]);
  const previewUrl = useMemo(
    () => (external ? '' : guestBlogOrderService.getPreviewUrl(documentUrl)),
    [external, documentUrl]
  );

  const resolvedFileName = useMemo(() => {
    if (external) {
      // External link — show a friendly description, not the URL fragment.
      if (
        fileName &&
        !/^https?:\/\//i.test(fileName) &&
        fileName !== 'document'
      ) {
        return fileName;
      }
      return friendlyExternalName(documentUrl);
    }
    const fromUrl = guestBlogOrderService.getFileName(documentUrl || '');
    if (
      fileName &&
      /\.[a-z0-9]{2,8}$/i.test(fileName) &&
      !/^https?:\/\//i.test(fileName)
    ) {
      return fileName;
    }
    return fromUrl || fileName || 'document';
  }, [fileName, documentUrl, external]);

  const effectiveFileType = useMemo((): 'pdf' | 'docx' | 'doc' | 'txt' | 'rtf' | 'unsupported' => {
    if (external) return 'unsupported'; // external URLs handled separately
    const fromResolved = getFileType(resolvedFileName);
    if (fromResolved !== 'unsupported') return fromResolved;
    const path = (documentUrl || '').split('?')[0];
    const lastSegment = path.split('/').pop() || '';
    return getFileType(lastSegment);
  }, [external, resolvedFileName, documentUrl]);

  // Load text content for text files
  const loadTextContent = async (url: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to load document');
      }

      const text = await response.text();
      setTextContent(text);
    } catch (err) {
      setError('Failed to load document content');
      console.error('Error loading text content:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load DOCX content using extraction service
  const loadDocxContent = async (pathOrUrl: string) => {
    try {
      setLoading(true);
      setError(null);

      const result = await guestBlogOrderService.extractDocxContent(pathOrUrl);

      if (result.success && result.content) {
        setDocxContent(result.content);
      } else {
        setError(`Extraction failed: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      setError('Failed to load document content');
      console.error('Error loading DOCX content:', err);
    } finally {
      setLoading(false);
    }
  };

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open || !documentUrl) {
      return;
    }

    setError(null);
    setTextContent(null);
    setDocxContent(null);
    setPdfOverlay(false);

    // External URL (Google Docs etc.) — embed directly, no API extract calls.
    if (external) {
      setLoading(false);
      if (googleDocsEmbed) {
        setPdfOverlay(true);
        const t = window.setTimeout(() => setPdfOverlay(false), 10000);
        return () => window.clearTimeout(t);
      }
      return;
    }

    const ft = effectiveFileType;

    if (ft === 'txt' || ft === 'rtf') {
      void loadTextContent(previewUrl || absoluteUrl);
      return;
    }

    if (ft === 'docx') {
      void loadDocxContent(documentUrl);
      return;
    }

    if (ft === 'doc') {
      setLoading(false);
      return;
    }

    if (ft === 'pdf') {
      setLoading(false);
      setPdfOverlay(true);
      const t = window.setTimeout(() => setPdfOverlay(false), 12000);
      return () => window.clearTimeout(t);
    }

    setLoading(false);
  }, [open, documentUrl, absoluteUrl, previewUrl, effectiveFileType, external, googleDocsEmbed]);

  const openFileInNewTab = () => {
    const target = external
      ? documentUrl
      : (previewUrl || absoluteUrl);
    window.open(target, '_blank', 'noopener,noreferrer');
  };

  // For external URLs we cannot programmatically download cross-origin —
  // fall back to opening the source in a new tab so the user can save it there.
  const handleDownloadClick = () => {
    if (external) {
      window.open(documentUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    onDownload?.();
  };
  const downloadAvailable = external || Boolean(onDownload);

  // Get preview content based on file type
  const renderPreviewContent = () => {
    const fileType = effectiveFileType;

    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading document...
          </Typography>
        </Box>
      );
    }

    // External link (Google Docs / Sheets / Slides / Drive / any web URL)
    if (external) {
      if (googleDocsEmbed) {
        return (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '600px',
              border: '1px solid #ddd',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            {pdfOverlay && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255,255,255,0.9)',
                }}
              >
                <CircularProgress size={36} />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Loading Google Docs preview…
                </Typography>
              </Box>
            )}
            <iframe
              src={googleDocsEmbed}
              width="100%"
              height="100%"
              style={{ border: 'none', borderRadius: '4px' }}
              title={`Preview: ${resolvedFileName}`}
              onLoad={() => setPdfOverlay(false)}
            />
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: 6,
                right: 10,
                bgcolor: 'rgba(255,255,255,0.85)',
                px: 1,
                borderRadius: 1,
                color: 'text.secondary',
              }}
            >
              The document must be shared as "Anyone with the link" to preview here.
            </Typography>
          </Box>
        );
      }

      // Non-Google external link — most sites block iframing, so offer open/download.
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            This is an external link. Open it in a new tab to view the content.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, wordBreak: 'break-all' }}>
            {documentUrl}
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={openFileInNewTab}
            startIcon={<ViewIcon />}
          >
            Open in new tab
          </Button>
        </Alert>
      );
    }

    if (fileType === 'pdf') {
      return (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '600px',
            border: '1px solid #ddd',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {pdfOverlay && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255,255,255,0.9)',
              }}
            >
              <CircularProgress size={36} />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Loading preview...
              </Typography>
            </Box>
          )}
          <iframe
            src={`${previewUrl || absoluteUrl}#toolbar=1&navpanes=0&scrollbar=1`}
            width="100%"
            height="100%"
            style={{ border: 'none', borderRadius: '4px' }}
            title={`PDF Preview: ${resolvedFileName}`}
            onLoad={() => setPdfOverlay(false)}
          />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          <Button size="small" onClick={openFileInNewTab} startIcon={<ViewIcon />} sx={{ ml: 1 }}>
            Open in new tab
          </Button>
          {downloadAvailable && (
            <Button
              size="small"
              onClick={handleDownloadClick}
              startIcon={<DownloadIcon />}
              sx={{ ml: 1 }}
            >
              Download
            </Button>
          )}
        </Alert>
      );
    }

    if (fileType === 'unsupported') {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            No inline preview for this file. Open it in a new tab to view quickly.
          </Typography>
          <Button variant="contained" size="small" onClick={openFileInNewTab} startIcon={<ViewIcon />}>
            Open file
          </Button>
          {downloadAvailable && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleDownloadClick}
              startIcon={<DownloadIcon />}
              sx={{ ml: 1 }}
            >
              Download
            </Button>
          )}
        </Alert>
      );
    }

    switch (fileType) {
      case 'docx':
        // For DOCX files, show extracted content if available
        if (docxContent) {
          return (
            <Box sx={{ width: '100%', height: '600px' }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>DOCX Content Preview:</strong> {resolvedFileName}
                </Typography>
                <Typography variant="body2">
                  Showing extracted content from the Word document. Download for full formatting and images.
                </Typography>
              </Alert>
              
              {/* Document Content */}
              <Box
                sx={{
                  width: '100%',
                  height: 'calc(100% - 80px)',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  p: 3,
                  bgcolor: '#ffffff',
                  overflow: 'auto',
                  boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    color: '#2b579a',
                    marginTop: '1em',
                    marginBottom: '0.5em',
                  },
                  '& p': {
                    marginBottom: '1em',
                    lineHeight: 1.6,
                  },
                  '& ul, & ol': {
                    marginLeft: '2em',
                    marginBottom: '1em',
                  },
                  '& table': {
                    borderCollapse: 'collapse',
                    width: '100%',
                    marginBottom: '1em',
                  },
                  '& td, & th': {
                    border: '1px solid #ddd',
                    padding: '8px',
                    textAlign: 'left',
                  },
                  '& th': {
                    backgroundColor: '#f5f5f5',
                    fontWeight: 'bold',
                  },
                  '& strong, & b': {
                    fontWeight: 600,
                  },
                  '& em, & i': {
                    fontStyle: 'italic',
                  },
                }}
                dangerouslySetInnerHTML={{ __html: docxContent }}
              />
            </Box>
          );
        } else {
          // Fallback to professional display if content extraction fails
          return (
            <Box sx={{ width: '100%', height: '600px' }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Microsoft Word Document:</strong> {resolvedFileName}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  {error || 'Loading document content...'}
                </Typography>
              </Alert>
              
              {/* Professional Document Display */}
              <Box sx={{ 
                width: '100%', 
                height: 'calc(100% - 80px)', 
                border: '2px dashed #e0e0e0', 
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Document Icon and Info */}
                <Box sx={{ textAlign: 'center', p: 4, zIndex: 1 }}>
                  <Box sx={{ 
                    fontSize: '4rem', 
                    mb: 2, 
                    color: '#2b579a',
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
                  }}>
                    📄
                  </Box>
                  
                  <Typography variant="h5" gutterBottom sx={{ color: '#2b579a', fontWeight: 600 }}>
                    Microsoft Word Document
                  </Typography>
                  
                  <Typography variant="h6" color="text.primary" gutterBottom sx={{ 
                    wordBreak: 'break-all',
                    maxWidth: '400px',
                    mx: 'auto'
                  }}>
                    {resolvedFileName}
                  </Typography>
                  
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: '500px', mx: 'auto' }}>
                    {error ? 'Content preview not available. ' : 'Loading document content... '}
                    Download to view the complete document with proper formatting.
                  </Typography>
                  
                  {/* Download Button */}
                  {onDownload && (
                    <Button
                      variant="contained"
                      size="large"
                      onClick={onDownload}
                      startIcon={<DownloadIcon />}
                      sx={{ 
                        mt: 2,
                        px: 4,
                        py: 1.5,
                        borderRadius: 3,
                        textTransform: 'none',
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                          boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                          transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      Download Document
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          );
        }

      case 'doc':
        // For older DOC files, show message that preview is not supported
        return (
          <Box sx={{ width: '100%', height: '600px' }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>DOC File Format:</strong> {resolvedFileName}
              </Typography>
              <Typography variant="body2">
                Preview is not available for older DOC format. Please download the file or convert to DOCX format for preview.
              </Typography>
            </Alert>
            
            {/* Professional Document Display */}
            <Box sx={{ 
              width: '100%', 
              height: 'calc(100% - 80px)', 
              border: '2px dashed #e0e0e0', 
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            }}>
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Box sx={{ fontSize: '4rem', mb: 2, color: '#2b579a' }}>📄</Box>
                <Typography variant="h5" gutterBottom sx={{ color: '#2b579a', fontWeight: 600 }}>
                  Microsoft Word Document (Legacy)
                </Typography>
                <Typography variant="h6" color="text.primary" gutterBottom>
                  {resolvedFileName}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: '500px', mx: 'auto' }}>
                  This is an older DOC format file. Preview is not supported for this format.
                </Typography>
                
                {onDownload && (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={onDownload}
                    startIcon={<DownloadIcon />}
                    sx={{ mt: 2 }}
                  >
                    Download Document
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        );

      case 'txt':
      case 'rtf':
        return (
          <Box
            sx={{
              width: '100%',
              minHeight: '400px',
              maxHeight: '600px',
              border: '1px solid #ddd',
              borderRadius: 1,
              p: 2,
              bgcolor: '#fafafa',
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: '14px',
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
            }}
          >
            {textContent || 'No content available'}
          </Box>
        );

      default:
        return (
          <Alert severity="info">
            <Typography variant="body1" gutterBottom>
              Preview not available for this file type.
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              File: {resolvedFileName}
            </Typography>
            <Button variant="contained" onClick={openFileInNewTab} startIcon={<ViewIcon />} sx={{ mt: 1, mr: 1 }}>
              Open file
            </Button>
            {onDownload && (
              <Button variant="outlined" onClick={onDownload} startIcon={<DownloadIcon />} sx={{ mt: 1 }}>
                Download File
              </Button>
            )}
          </Alert>
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh', maxHeight: '800px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <ViewIcon color="primary" />
            <Typography variant="h6" component="span">
              Document Preview
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {resolvedFileName}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 2, overflow: 'hidden' }}>
        {renderPreviewContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={openFileInNewTab} startIcon={<ViewIcon />} variant="outlined">
          Open in new tab
        </Button>
        {downloadAvailable && (
          <Button onClick={handleDownloadClick} startIcon={<DownloadIcon />} variant="outlined">
            Download
          </Button>
        )}
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentPreviewModal;
